import React, { useState } from 'react';
import { Award, Calendar, Clock, Linkedin, Mail, Phone, Shield, Sparkles } from 'lucide-react';
import { doctorCards } from '../data/doctorCards';

const DoctorCardGrid = () => {
  const [hoveredDoctor, setHoveredDoctor] = useState(null);

  const handleDoctorHover = (doctorId) => setHoveredDoctor(doctorId);
  const handleDoctorLeave = () => setHoveredDoctor(null);

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50"></div>
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `linear-gradient(rgba(59,130,246,0.08) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(59,130,246,0.08) 1px, transparent 1px)`,
          backgroundSize: '120px 120px'
        }}
      ></div>
      <div className="absolute -right-10 top-10 w-60 h-60 bg-blue-100/40 rounded-full blur-3xl"></div>
      <div className="absolute -left-16 bottom-0 w-72 h-72 bg-purple-100/40 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-6 py-2 bg-white/80 border border-blue-100 rounded-full shadow-md mb-4">
            <Sparkles className="w-4 h-4 text-blue-500 mr-2" />
            Trusted Orthopedic Leadership
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            Our Medical Specialists
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Each consultant blends international training with compassionate bedside care to deliver outstanding orthopedic outcomes.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {doctorCards.map((doctor) => (
            <div
              key={doctor.id}
              className="group relative bg-white/90 backdrop-blur rounded-3xl shadow-lg border border-blue-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
              onMouseEnter={() => handleDoctorHover(doctor.id)}
              onMouseLeave={handleDoctorLeave}
            >
              <div className={`transition-opacity duration-300 ${hoveredDoctor === doctor.id ? 'opacity-0' : 'opacity-100'}`}>
                <div className="px-6 pt-8 pb-7 text-center space-y-5">
                  <div className="relative mx-auto w-24 h-24 rounded-2xl overflow-hidden shadow-lg ring-4 ring-blue-100">
                    <img src={doctor.image} alt={doctor.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{doctor.name}</h3>
                    <p className="text-blue-600 font-semibold text-sm leading-snug mt-1">{doctor.role}</p>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed min-h-[60px]">{doctor.specialization}</p>
                  <div className="flex justify-center flex-wrap gap-2 text-xs text-blue-700 font-semibold">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 border border-blue-100">
                      <Award className="w-3.5 h-3.5 mr-1" /> {doctor.experience}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-50 border border-purple-100 text-purple-700">
                      <Shield className="w-3.5 h-3.5 mr-1" /> Board Certified
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    <p className="font-medium">{doctor.education}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-100 flex flex-col items-center space-y-1 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1 text-blue-500" />
                      {doctor.availability}
                    </div>
                    <div>{doctor.languages.join(' • ')}</div>
                  </div>
                </div>
              </div>

              <div className={`absolute inset-0 bg-gradient-to-br from-blue-700 to-blue-900 text-white p-6 rounded-3xl transition-opacity duration-300 ${hoveredDoctor === doctor.id ? 'opacity-100' : 'opacity-0'}`}>
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <h4 className="font-bold mb-3 text-lg">Expertise & Achievements</h4>
                    <div className="space-y-2 text-sm text-blue-100">
                      {doctor.expertise.slice(0, 4).map((skill, index) => (
                        <div key={index} className="flex items-center">
                          <span className="w-1.5 h-1.5 bg-yellow-300 rounded-full mr-2"></span>
                          {skill}
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-5">
                      <h5 className="font-semibold mb-2">Key Achievements</h5>
                      <ul className="text-xs space-y-1 text-blue-100/90">
                        {doctor.achievements.slice(0, 2).map((achievement, index) => (
                          <li key={index} className="text-blue-100">• {achievement}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3">
                    <div className="flex justify-center space-x-4">
                      <a href={`mailto:${doctor.contact.email}`} className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                        <Mail className="h-4 w-4" />
                      </a>
                      <a href={`tel:${doctor.contact.phone}`} className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                        <Phone className="h-4 w-4" />
                      </a>
                      <a
                        href={`https://linkedin.com/in/${doctor.contact.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                      >
                        <Linkedin className="h-4 w-4" />
                      </a>
                    </div>
                    <button className="w-full py-2.5 px-4 bg-yellow-400 text-primary-900 font-semibold rounded-lg hover:bg-yellow-300 transition-colors shadow-lg shadow-yellow-500/30">
                      <Calendar className="h-4 w-4 inline mr-1" />
                      Book Appointment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DoctorCardGrid;

